/*#
# Practices: triggers and modes

A practice acts on the board when something happens to a task (board
#2474). What it does is declared, the project has to opt in, every change is
attributed to the practice, and an automatic change undoes in one step.

- **Trigger:** tags on the practice say which events fire it:
  `trigger:created`, `trigger:enters-inbox`, `trigger:status:<value>`,
  `trigger:tagged:<key>`, `trigger:closed`, `trigger:reopened` and
  `trigger:commented`.
- **Procedure:** `virta procedure #n --when "<filter>" --set kind:bug`. The
  task must match the filter, and it gets the tags.
- **Mode:** `mode:manual` never runs itself. `mode:suggest` proposes the
  tags on the task, for a person to accept or overrule. `mode:automatic`
  sets them itself, recorded so one `resolve … undo` reverses exactly that.
- **Opt-in:** `virta project set <name> --practices "#12 #40"`. A practice
  never touches a project that hasn't opted in, and only acts on what
  happened after it did.
- **Running:** `virta project practices [name]` (and the MCP tool)
  evaluates the opted-in projects. Every event it writes has an id made
  from the practice and the event that fired it, so running it twice, or
  on two machines, writes nothing twice.
- **Cascades:** a practice's own writes don't fire practices, unless one
  declares `chains-from:#n`.

Every outcome (suggested, accepted, overruled, applied, undone) is on the
task and counted on the practice, under the measure in force when it
happened: the evidence its measure reads (board #2475). The practice's
**evidence grade** moves with that record: `decree` until it has been
applied, `observed` once it has (or while it has no measure), `measured`
once it has a measure and ten outcomes under it.
*/

/*{ "parent": "Reference", "order": 37 }*/

import type { EventDraft, Task, VirtaEvent } from './model'
import { projectEventId } from './model'
import type { VirtaStore } from './store'
import {
  compileFilter,
  EXCLUSIVE_KEYS,
  parseTag,
  tagValues,
  TYPE_KEY,
} from './tags'

/** The tag key a practice's triggers use. */
export const TRIGGER_KEY = 'trigger'
/** The tag key a practice declares a chain with: `chains-from:#12`. */
export const CHAIN_KEY = 'chains-from'

/** Who a practice's writes say they are: `practice #12 via <the runner>`. */
export const practiceBy = (practice: Task, runner: string) =>
  `practice #${practice.num} via ${runner}`

/** Whether an event was written by a practice. */
const byPractice = (event: VirtaEvent) => /^practice #\d+ via /.test(event.by)

/** Which practice (by number) wrote an event, if one did. */
const practiceNumOf = (event: VirtaEvent) =>
  /^practice #(\d+) via /.exec(event.by)?.[1] ?? ''

/** Whether an event fires a trigger (a `trigger:` tag's value). */
export function fires(trigger: string, event: VirtaEvent): boolean {
  const p = event.payload as { tag?: string; tags?: string[] }
  switch (trigger) {
    case 'created':
      return event.kind === 'created'
    case 'enters-inbox':
      return (
        event.kind === 'created' &&
        !(p.tags ?? []).some((t) => t.startsWith('status:'))
      )
    case 'closed':
    case 'reopened':
    case 'commented':
      return event.kind === trigger
  }
  if (trigger.startsWith('status:'))
    return event.kind === 'tagged' && p.tag === trigger
  if (trigger.startsWith('tagged:')) {
    const key = trigger.slice('tagged:'.length)
    return event.kind === 'tagged' && !!p.tag && parseTag(p.tag).key === key
  }
  return false
}

/** Whether a project's `practices` setting lists a practice — by `#n` (what people type) or by id. */
function lists(setting: string | undefined, practice: Task): boolean {
  const listed = (setting ?? '').split(/[\s,]+/).filter(Boolean)
  return listed.includes(practice.id) || listed.includes(`#${practice.num}`)
}

/** When a project opted in to a practice: the first settings change that listed it, or ''. */
function optedInAt(store: VirtaStore, project: string, practice: Task): string {
  for (const e of store.events(projectEventId(project))) {
    if (e.kind !== 'project') continue
    if (lists(e.payload.settings.practices, practice)) return e.at
  }
  return ''
}

/** The projects a practice runs in: those that list it in their `practices` setting. */
function projectsFor(store: VirtaStore, practice: Task): string[] {
  return store.projects
    .filter((p) => lists(p.practices, practice))
    .map((p) => p.name)
}

export interface PracticePlan {
  /** the events to write, with ids made from the practice and the event that fired it */
  drafts: EventDraft[]
  /** one line per application, for a dry run and the CLI */
  lines: string[]
}

/**
 * What the practices would do now (pure: reads the store, writes nothing).
 * Only practices in suggest or automatic mode with a procedure and a
 * trigger act; only on tasks in projects that opted in; only on events
 * after the project opted in and after the procedure was set; never on a
 * practice's own writes unless chained; never twice (an id the store
 * holds is skipped).
 */
export function planPractices(
  store: VirtaStore,
  options: { project?: string; runner: string }
): PracticePlan {
  const drafts: EventDraft[] = []
  const lines: string[] = []
  const practices = store
    .query(`${TYPE_KEY}:any is:open`, null)
    .filter(
      (t) =>
        ['practice', 'policy'].includes(tagValues(t.tags, TYPE_KEY)[0] ?? '') &&
        t.procedure &&
        ['suggest', 'automatic'].includes(tagValues(t.tags, 'mode')[0] ?? '')
    )
  for (const practice of practices) {
    const procedure = practice.procedure!
    const mode = tagValues(practice.tags, 'mode')[0]!
    const triggers = tagValues(practice.tags, TRIGGER_KEY)
    if (!triggers.length) continue
    const chains = new Set(
      tagValues(practice.tags, CHAIN_KEY).map((v) => v.replace(/^#/, ''))
    )
    const when = compileFilter(procedure.when)
    const by = practiceBy(practice, options.runner)
    for (const project of projectsFor(store, practice)) {
      if (options.project && project !== options.project) continue
      const since = [optedInAt(store, project, practice), procedure.at]
        .sort()
        .pop()!
      for (const task of store.query(
        `project:${project} is:any archived:any`,
        null
      )) {
        for (const event of store.events(task.id)) {
          if (event.at < since) continue
          if (!triggers.some((t) => fires(t, event))) continue
          // a practice's own writes fire nothing, unless chained on purpose
          if (byPractice(event) && !chains.has(practiceNumOf(event))) continue
          if (!when(task)) continue
          const missing = procedure.set.filter((t) => !task.tags.includes(t))
          if (!missing.length) continue
          const base = `practice:${practice.id}:${event.id}`
          const kindOf = mode === 'suggest' ? 'suggested' : 'practiced'
          if (store.holds(`${base}:${kindOf}`)) continue
          // the fired event's time: the same draft from any runner
          const at = event.at
          if (mode === 'suggest') {
            drafts.push({
              id: `${base}:suggested`,
              taskId: task.id,
              at,
              by,
              kind: 'suggested',
              payload: {
                practice: practice.id,
                trigger: event.id,
                set: missing,
                text: `#${practice.num} ${
                  practice.title
                } suggests ${missing.join(' ')}`,
              },
            } as EventDraft)
            lines.push(
              `#${task.num}: #${practice.num} suggests ${missing.join(' ')}`
            )
          } else {
            // one value per exclusive key: what a set replaces is removed
            const removed = task.tags.filter((t) => {
              const { key, value } = parseTag(t)
              return (
                value !== null &&
                missing.some((m) => {
                  const n = parseTag(m)
                  return (
                    n.key === key &&
                    n.value !== value &&
                    EXCLUSIVE_KEYS.includes(key)
                  )
                })
              )
            })
            drafts.push(
              ...removed.map(
                (tag, i) =>
                  ({
                    id: `${base}:untag:${i}`,
                    taskId: task.id,
                    at,
                    by,
                    kind: 'untagged',
                    payload: { tag },
                  } as EventDraft)
              ),
              ...missing.map(
                (tag, i) =>
                  ({
                    id: `${base}:tag:${i}`,
                    taskId: task.id,
                    at,
                    by,
                    kind: 'tagged',
                    payload: { tag },
                  } as EventDraft)
              ),
              {
                id: `${base}:practiced`,
                taskId: task.id,
                at,
                by,
                kind: 'practiced',
                payload: {
                  practice: practice.id,
                  trigger: event.id,
                  added: missing,
                  removed,
                },
              } as EventDraft
            )
            lines.push(
              `#${task.num}: #${practice.num} set ${missing.join(' ')}${
                removed.length ? ` (removed ${removed.join(' ')})` : ''
              }`
            )
          }
        }
      }
    }
  }
  return { drafts, lines }
}

/** How many resolved outcomes under the current measure make a practice measured. */
export const MEASURED_AFTER = 10

/** Where a practice's evidence stands (board #2475): decree, observed or measured. */
export type EvidenceGrade = 'decree' | 'observed' | 'measured'

/** A practice's evidence grade, from its record and its measure. */
export function evidenceGrade(
  practice: Pick<Task, 'record' | 'measure'>
): EvidenceGrade {
  const r = practice.record
  if (!r || r.suggested + r.applied === 0) return 'decree'
  if (!practice.measure) return 'observed'
  const now = r.byMeasure[String(practice.measure.version)]
  const resolved = now ? now.accepted + now.overruled + now.undone : 0
  return resolved >= MEASURED_AFTER ? 'measured' : 'observed'
}

/** A practice's record in a line: its outcomes under the current measure, and its grade. */
export function recordLine(practice: Pick<Task, 'record' | 'measure'>): string {
  const grade = evidenceGrade(practice)
  const r = practice.record
  if (!r) return `${grade}: not applied yet`
  const v = String(practice.measure?.version ?? 0)
  const c = r.byMeasure[v] ?? {
    suggested: 0,
    accepted: 0,
    overruled: 0,
    applied: 0,
    undone: 0,
  }
  const parts = [
    c.suggested && `${c.suggested} suggested`,
    c.accepted && `${c.accepted} accepted`,
    c.overruled && `${c.overruled} overruled`,
    c.applied && `${c.applied} applied`,
    c.undone && `${c.undone} undone`,
  ].filter(Boolean)
  return `${grade}: ${parts.length ? parts.join(', ') : 'nothing yet'}${
    practice.measure ? ` (measure v${v})` : ''
  }`
}

// ── refactoring: split, supersede, merge, retire (board #2496) ─────────────

/** The relation tags between a practice and what replaced it: one end each, the other found by query. */
export const SPLIT_FROM = 'split-from'
export const SUPERSEDED_BY = 'superseded-by'
export const MERGED_INTO = 'merged-into'

/** Whether a task is a Library entry: a practice, policy or guidance. */
export const isEntry = (t: Pick<Task, 'tags'>) =>
  ['practice', 'policy', 'guidance'].includes(
    tagValues(t.tags, TYPE_KEY)[0] ?? ''
  )

const numbered = (tags: readonly string[], key: string) =>
  tagValues(tags, key)
    .map((v) => Number(/^#(\d+)$/.exec(v)?.[1] ?? NaN))
    .filter((n) => Number.isInteger(n))

/**
 * A practice's lineage (board #2496): what it came from (`earlier`, whose
 * evidence it shows read-only) and what replaced it (`later`). Each
 * relation is one tag on one end; the other end is found here.
 */
export function lineage(
  store: VirtaStore,
  task: Task
): { earlier: Task[]; later: Task[] } {
  const ref = `#${task.num}`
  const all = store.tasks
  const byNum = (n: number) => store.byNum(n)
  const earlier = [
    ...numbered(task.tags, SPLIT_FROM).map(byNum),
    ...all.filter(
      (t) =>
        t.tags.includes(`${SUPERSEDED_BY}:${ref}`) ||
        t.tags.includes(`${MERGED_INTO}:${ref}`)
    ),
  ]
  const later = [
    ...all.filter((t) => t.tags.includes(`${SPLIT_FROM}:${ref}`)),
    ...numbered(task.tags, SUPERSEDED_BY).map(byNum),
    ...numbered(task.tags, MERGED_INTO).map(byNum),
  ]
  const unique = (list: (Task | undefined)[]) => [
    ...new Map(
      list.filter((t): t is Task => !!t).map((t) => [t.id, t])
    ).values(),
  ]
  return { earlier: unique(earlier), later: unique(later) }
}

/** The tags a successor inherits: what it is and what it's about, never what makes it act. */
function inherited(tags: readonly string[]): string[] {
  return tags.filter((t) => /^(type|topic|project|rung|area):/.test(t))
}

/** A Library entry by #n or id, or why not. */
function entryOrWhy(store: VirtaStore, ref: string): Task | string {
  const m = /^#?(\d+)$/.exec(ref.trim())
  const task = m ? store.byNum(Number(m[1])) : store.get(ref)
  if (!task) return `no task ${ref}`
  if (!isEntry(task))
    return `#${task.num} is a task, not a practice, policy or guidance`
  return task
}

/**
 * Split a practice into new ones (board #2496): each successor is created
 * with its type, topics and project, tagged `split-from:#A`, and A is
 * retired. Successors never inherit a mode, trigger or procedure: the
 * owner switches them on. One commit: all of it, or none.
 */
export async function splitEntry(
  store: VirtaStore,
  ref: string,
  titles: string[]
): Promise<{ retired: Task; successors: Task[] }> {
  const a = entryOrWhy(store, ref)
  if (typeof a === 'string') throw new Error(a)
  if (a.closedAt) throw new Error(`#${a.num} is already closed`)
  if (!titles.length) throw new Error('split needs the new titles')
  const ids: string[] = []
  await store.transaction(async (scratch) => {
    for (const title of titles) {
      const t = await scratch.create({
        title,
        body: `Split from #${a.num} (${a.title}).`,
        tags: [...inherited(a.tags), `${SPLIT_FROM}:#${a.num}`],
      })
      ids.push(t.id)
    }
    const nums = ids.map((id) => `#${scratch.get(id)!.num}`)
    await scratch.close(a.id, `split into ${nums.join(', ')}`, 'retired')
  })
  return {
    retired: store.get(a.id)!,
    successors: ids.map((id) => store.get(id)!),
  }
}

/**
 * Supersede: A is replaced by B (board #2496). A is tagged
 * `superseded-by:#B` and retired; B is untouched (it starts its own record
 * and shows A's as earlier evidence).
 */
export async function supersedeEntry(
  store: VirtaStore,
  ref: string,
  byRef: string
): Promise<Task> {
  const a = entryOrWhy(store, ref)
  if (typeof a === 'string') throw new Error(a)
  const b = entryOrWhy(store, byRef)
  if (typeof b === 'string') throw new Error(b)
  if (a.id === b.id) throw new Error('a practice cannot supersede itself')
  await store.transaction(async (scratch) => {
    await scratch.tag(a.id, `${SUPERSEDED_BY}:#${b.num}`)
    if (!a.closedAt)
      await scratch.close(a.id, `superseded by #${b.num}`, 'retired')
  })
  return store.get(a.id)!
}

/**
 * Merge several into one (board #2496): each is tagged `merged-into:#C`
 * and retired. C must be open.
 */
export async function mergeEntries(
  store: VirtaStore,
  refs: string[],
  intoRef: string
): Promise<Task[]> {
  const into = entryOrWhy(store, intoRef)
  if (typeof into === 'string') throw new Error(into)
  if (into.closedAt)
    throw new Error(`#${into.num} is closed: merge into an open one`)
  const parts: Task[] = []
  for (const ref of refs) {
    const t = entryOrWhy(store, ref)
    if (typeof t === 'string') throw new Error(t)
    if (t.id === into.id) throw new Error('a practice cannot merge into itself')
    parts.push(t)
  }
  if (!parts.length) throw new Error('merge needs what to merge')
  await store.transaction(async (scratch) => {
    for (const t of parts) {
      await scratch.tag(t.id, `${MERGED_INTO}:#${into.num}`)
      if (!t.closedAt)
        await scratch.close(t.id, `merged into #${into.num}`, 'retired')
    }
  })
  return parts.map((t) => store.get(t.id)!)
}
